import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRegistros, getDepartamentos } from '../services/api';

const MESES_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho',
                  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const diasNoMes = (ano, mes) => new Date(ano, mes, 0).getDate();

const OCORRENCIAS = [
  { key: 'entrada_atrasada', label: 'Entrada\nAtrasada',  title: 'Chegada >15min após o horário contratado', color: '#ca8a04', bg: 'rgba(234,179,8,0.10)',    icon: 'bi-alarm-fill' },
  { key: 'almoco_tardio',    label: 'Almoço\nTardio',    title: 'Saiu para almoço após 5h de jornada',       color: '#f97316', bg: 'rgba(251,146,60,0.10)',   icon: 'bi-exclamation-triangle-fill' },
  { key: 'almoco_precoce',   label: 'Almoço\nPrecoce',   title: 'Saiu para almoço antes de 4h de jornada',  color: '#3b82f6', bg: 'rgba(59,130,246,0.10)',    icon: 'bi-clock-fill' },
  { key: 'almoco_curto',     label: 'Almoço\nCurto',     title: 'Intervalo de almoço menor que 1 hora',      color: '#a855f7', bg: 'rgba(168,85,247,0.10)',    icon: 'bi-cup-hot-fill' },
  { key: 'saida_apos_1815',  label: 'Saída\n>18h15',     title: 'Saída registrada após as 18h15',            color: '#dc2626', bg: 'rgba(239,68,68,0.10)',     icon: 'bi-moon-stars-fill' },
];

export default function OcorrenciasJornada() {
  const anoAtual  = new Date().getFullYear();
  const mesAtual  = new Date().getMonth() + 1;

  const [mes,       setMes]       = useState(mesAtual);
  const [ano,       setAno]       = useState(anoAtual);
  const [filtroDep, setFiltroDep] = useState('');
  const [buscaNome, setBuscaNome] = useState('');
  const [deptos,    setDeptos]    = useState([]);
  const [dados,     setDados]     = useState([]);   // [{ id, nome, depto, ...counts, total }]
  const [loading,   setLoading]   = useState(false);
  const [ordenar,   setOrdenar]   = useState({ col: 'total', dir: 'desc' });
  const navigate = useNavigate();

  const anos = [];
  for (let y = anoAtual - 3; y <= anoAtual + 1; y++) anos.push(y);

  useEffect(() => {
    getDepartamentos({ ativo: true }).then(r => setDeptos(r.data.results || r.data)).catch(() => {});
  }, []);

  const buscar = useCallback(async () => {
    setLoading(true);
    setDados([]);
    try {
      const pad   = n => String(n).padStart(2, '0');
      const total = diasNoMes(ano, mes);
      const ini   = `${pad(1)}/${pad(mes)}/${ano}`;
      const fim   = `${pad(total)}/${pad(mes)}/${ano}`;

      // Busca todos os registros do mês (paginação automática)
      let todos = [];
      let url   = { data_inicio: ini, data_fim: fim, page_size: 500 };
      let page  = 1;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const res  = await getRegistros({ ...url, page });
        const list = res.data.results || res.data;
        todos = [...todos, ...list];
        if (!res.data.next) break;
        page++;
      }

      // Agrega por funcionário
      const mapa = {};
      todos.forEach(r => {
        const fid = r.funcionario;
        if (!mapa[fid]) {
          mapa[fid] = {
            id:    fid,
            nome:  r.funcionario_nome  || `#${fid}`,
            depto: r.funcionario_departamento_nome || '—',
            entrada_atrasada: 0,
            almoco_tardio:    0,
            almoco_precoce:   0,
            almoco_curto:     0,
            saida_apos_1815:  0,
          };
        }
        OCORRENCIAS.forEach(({ key }) => { if (r[key]) mapa[fid][key]++; });
      });

      let lista = Object.values(mapa)
        .map(f => ({ ...f, total: OCORRENCIAS.reduce((s, { key }) => s + f[key], 0) }))
        .filter(f => f.total > 0 || todos.some(r => r.funcionario === f.id));

      // Filtro de setor
      if (filtroDep) lista = lista.filter(f => {
        const dep = deptos.find(d => String(d.id) === String(filtroDep));
        return dep ? f.depto === dep.nome : true;
      });

      setDados(lista);
    } finally {
      setLoading(false);
    }
  }, [mes, ano, filtroDep, deptos]);

  useEffect(() => { buscar(); }, [buscar]);

  // Ordenação
  const toggleOrdem = (col) => {
    setOrdenar(o => o.col === col
      ? { col, dir: o.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: 'desc' }
    );
  };

  const dadosVisiveis = [...dados]
    .filter(f => !buscaNome || f.nome.toLowerCase().includes(buscaNome.toLowerCase()))
    .sort((a, b) => {
      const v = ordenar.dir === 'asc' ? 1 : -1;
      if (typeof a[ordenar.col] === 'string')
        return a[ordenar.col].localeCompare(b[ordenar.col]) * v;
      return (a[ordenar.col] - b[ordenar.col]) * v;
    });

  // Totalizadores das colunas
  const totais = OCORRENCIAS.reduce((acc, { key }) => {
    acc[key] = dados.reduce((s, f) => s + f[key], 0);
    return acc;
  }, {});
  totais.total = dados.reduce((s, f) => s + f.total, 0);

  const thStyle = (col) => ({
    textAlign: 'center', fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.4px', padding: '8px 6px',
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'pre-line',
    borderBottom: ordenar.col === col ? '2px solid #2563eb' : '1px solid #f1f5f9',
  });

  const arrow = (col) => ordenar.col === col
    ? <i className={`bi bi-caret-${ordenar.dir === 'asc' ? 'up' : 'down'}-fill`} style={{ fontSize: '0.55rem', marginLeft: 3 }} />
    : null;

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 16, marginBottom: 20, borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 38, height: 38, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97706', fontSize: '1rem', marginRight: 14, flexShrink: 0 }}>
            <i className="bi bi-exclamation-diamond-fill" />
          </div>
          <div>
            <div style={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Análises · Controle de Ponto</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#334155', letterSpacing: '-0.5px', lineHeight: 1.2 }}>Ocorrências de Jornada</div>
          </div>
        </div>
        {!loading && dados.length > 0 && (
          <span style={{ fontSize: '0.75rem', color: '#64748b', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '4px 12px', fontWeight: 600 }}>
            {dadosVisiveis.length} funcionário(s) · {totais.total} ocorrência(s)
          </span>
        )}
      </div>

      {/* Filtros */}
      <div className="filter-bar mb-3" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div style={{ minWidth: 160 }}>
          <label className="form-label">Setor</label>
          <select className="form-select" value={filtroDep} onChange={e => setFiltroDep(e.target.value)}>
            <option value="">Todos</option>
            {deptos.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 130 }}>
          <label className="form-label">Mês</label>
          <select className="form-select" value={mes} onChange={e => setMes(Number(e.target.value))}>
            {MESES_PT.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 90 }}>
          <label className="form-label">Ano</label>
          <select className="form-select" value={ano} onChange={e => setAno(Number(e.target.value))}>
            {anos.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label className="form-label">Funcionário</label>
          <input className="form-control" placeholder="Filtrar por nome..." value={buscaNome} onChange={e => setBuscaNome(e.target.value)} />
        </div>
      </div>

      {/* Legenda */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {OCORRENCIAS.map(o => (
          <span key={o.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: o.bg, border: `1px solid ${o.color}33`, borderRadius: 20, padding: '3px 10px', fontSize: '0.68rem', fontWeight: 600, color: o.color }}>
            <i className={`bi ${o.icon}`} style={{ fontSize: '0.6rem' }} />
            {o.title}
          </span>
        ))}
      </div>

      {/* Tabela */}
      <div className="card-ponto p-0">
        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: '56px 0', textAlign: 'center', color: '#94a3b8' }}>
              <span className="spinner-border spinner-border-sm me-2" />Carregando ocorrências...
            </div>
          ) : dadosVisiveis.length === 0 ? (
            <div style={{ padding: '56px 0', textAlign: 'center', color: '#94a3b8' }}>
              <i className="bi bi-check-circle" style={{ fontSize: '2.5rem', display: 'block', marginBottom: 10, color: '#86efac' }} />
              <p style={{ margin: 0, fontSize: '0.85rem' }}>Nenhuma ocorrência encontrada para o período.</p>
            </div>
          ) : (
            <table style={{ width: '100%', fontSize: '0.72rem', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 40 }} />
                <col style={{ width: 240 }} />
                <col style={{ width: 160 }} />
                {OCORRENCIAS.map(o => <col key={o.key} style={{ width: 80 }} />)}
                <col style={{ width: 70 }} />
              </colgroup>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ ...thStyle(null), paddingLeft: 16 }}>#</th>
                  <th style={{ ...thStyle('nome'), textAlign: 'left', paddingLeft: 10 }} onClick={() => toggleOrdem('nome')}>
                    Funcionário{arrow('nome')}
                  </th>
                  <th style={{ ...thStyle('depto'), textAlign: 'left' }} onClick={() => toggleOrdem('depto')}>
                    Setor{arrow('depto')}
                  </th>
                  {OCORRENCIAS.map(o => (
                    <th key={o.key} style={{ ...thStyle(o.key), color: o.color }} onClick={() => toggleOrdem(o.key)}>
                      <i className={`bi ${o.icon}`} style={{ display: 'block', marginBottom: 2 }} />
                      {o.label}{arrow(o.key)}
                    </th>
                  ))}
                  <th style={{ ...thStyle('total'), color: '#1e293b', fontWeight: 800 }} onClick={() => toggleOrdem('total')}>
                    Total{arrow('total')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {dadosVisiveis.map((f, i) => {
                  const bg = i % 2 === 0 ? '#fff' : '#f8fafc';
                  return (
                    <tr key={f.id} style={{ background: bg, borderBottom: '1px solid #f1f5f9' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                      onMouseLeave={e => e.currentTarget.style.background = bg}>
                      <td style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.65rem', paddingLeft: 16 }}>{i + 1}</td>
                      <td style={{ paddingLeft: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        onClick={() => navigate('/ponto-individual', { state: { funcionarioId: f.id, mes, ano } })}
                        title="Ver ponto individual">
                        <span style={{ fontWeight: 600, color: '#334155', cursor: 'pointer' }}>
                          {f.nome}
                        </span>
                      </td>
                      <td style={{ color: '#64748b', fontSize: '0.68rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.depto}</td>
                      {OCORRENCIAS.map(o => (
                        <td key={o.key} style={{ textAlign: 'center', background: f[o.key] > 0 ? o.bg : 'transparent' }}>
                          {f[o.key] > 0
                            ? <span style={{ fontWeight: 700, color: o.color }}>{f[o.key]}</span>
                            : <span style={{ color: '#e2e8f0' }}>—</span>}
                        </td>
                      ))}
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ fontWeight: 800, color: f.total > 5 ? '#dc2626' : f.total > 2 ? '#f97316' : '#64748b', background: f.total > 5 ? 'rgba(239,68,68,0.08)' : f.total > 2 ? 'rgba(251,146,60,0.08)' : 'transparent', borderRadius: 4, padding: '1px 6px' }}>
                          {f.total}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Linha de totais */}
              <tfoot>
                <tr style={{ background: '#f0f4ff', borderTop: '2px solid #bfdbfe' }}>
                  <td colSpan={3} style={{ paddingLeft: 10, fontSize: '0.68rem', fontWeight: 700, color: '#475569', padding: '8px 10px' }}>TOTAL DO MÊS</td>
                  {OCORRENCIAS.map(o => (
                    <td key={o.key} style={{ textAlign: 'center', padding: '8px 0' }}>
                      <span style={{ fontWeight: 700, color: o.color }}>{totais[o.key] || '—'}</span>
                    </td>
                  ))}
                  <td style={{ textAlign: 'center', padding: '8px 0' }}>
                    <span style={{ fontWeight: 800, color: '#1e293b' }}>{totais.total}</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
